import {
  DeleteOutlined,
  PlusOutlined,
  SwapOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { message } from "antd";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { CREATE_jwel1, CREATE_jwel } from "../../Config/Config";
import styles from "./UploadFile.module.css";

const MAX_SIZE_MB = 10;
const TABLE_NAME = "Firm_Configure";
const IMAGE_BASE = "https://image.timeserasoftware.in";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toJpegBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.92).split(",")[1]);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const formatSize = (bytes) => {
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) return mb.toFixed(2) + "MB";
  return (bytes / 1024).toFixed(2) + "KB";
};

const getExt = (name) => {
  const parts = name.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "file";
};

// ─── Main component ───────────────────────────────────────────────────────────
export const UploadFile = ({
  title = "Upload Files",
  accent = "#2563eb",
  keyPrefix = "EPASS1", // DB column key  e.g. "EPASS1" | "EPASS2" | "ECAT_IMAGE"
  fileBaseName = "EPASS", // CDN file base  → EPASS1.jpg, EPASS2.jpg, EPASS3.jpg
  folder = "BANNERS",
  mode = "banner", // "banner" | "logo"
  maxImages, // number → capped mode (limits count, no auto-recycle). omitted/undefined → uncapped
}) => {
  // banner mode is "capped" only when a positive maxImages was actually passed
  const isCapped = mode === "banner" && Number.isFinite(maxImages) && maxImages > 0;

  // ── BANNER: queue of staged new items ──────────────────────────────────────
  // [{ id, file, preview, name, ext, size }]
  const [stagedFiles, setStagedFiles] = useState([]);

  // ── LOGO: single staged item ───────────────────────────────────────────────
  const [stagedFile, setStagedFile] = useState(null);

  const [dragActive, setDragActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingImages, setExistingImages] = useState([]); // [{ slotNum, url }]
  const [existingLogo, setExistingLogo] = useState("");
  const [originalFirmName, setOriginalFirmName] = useState("");

  // ── BANNER (capped & uncapped): in-place replace + delete of saved images ──
  const [replacements, setReplacements] = useState({}); // { [slotNum]: { file, preview, name, ext, size } }
  const [deletedSlots, setDeletedSlots] = useState(new Set());

  const inputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replaceTargetSlot = useRef(null);
  const tenantName = localStorage.getItem("tenantName");

  // ── fetch ─────────────────────────────────────────────────────────────────
  const fetchExisting = useCallback(async () => {
    try {
      const res = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=${TABLE_NAME}`,
        { headers: { tenantName } },
      );
      const row = Array.isArray(res.data) ? res.data[0] : res.data;
      if (!row) return;

      setOriginalFirmName(row.FIRMNAME);
      const bust = Date.now();
      const rawValue = row[keyPrefix] || "";

      if (mode === "banner") {
        const urls = rawValue
          .split(",")
          .map((u) => u.trim())
          .filter((u) => u && u !== "-");
        // Capped mode: safety net — keep only the most recent maxImages entries
        // so the list never shows more than the cap even if the DB has stale extras.
        // Uncapped mode: keep everything, count grows freely.
        const kept = isCapped ? urls.slice(-maxImages) : urls;
        setExistingImages(
          kept.map((url) => {
            const match = url.match(
              new RegExp(`${fileBaseName}(\\d+)\\.jpg$`, "i"),
            );
            return {
              slotNum: match ? parseInt(match[1], 10) : 0,
              url: `${url}?v=${bust}`,
            };
          }),
        );
        // Reset any in-progress replace/delete edits for both capped & uncapped.
        setDeletedSlots(new Set());
        setReplacements({});
      } else {
        const url = rawValue && rawValue !== "-" ? rawValue : "";
        setExistingLogo(url ? `${url}?v=${bust}` : "");
      }
    } catch (err) {
      console.error("Fetch existing failed:", err);
    }
  }, [keyPrefix, mode, tenantName, isCapped, fileBaseName, maxImages]);

  useEffect(() => {
    fetchExisting();
  }, [fetchExisting]);

  // ── staging helpers ────────────────────────────────────────────────────────
  const buildItem = (file) => ({
    id: `${Date.now()}-${Math.random()}`,
    file,
    preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    name: file.name.replace(/\.[^/.]+$/, ""),
    ext: getExt(file.name),
    size: formatSize(file.size),
  });

  const validateFile = (f) => {
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      message.warning(`"${f.name}" exceeds ${MAX_SIZE_MB}MB — skipped.`);
      return false;
    }
    return true;
  };

  // How many more NEW files we can still queue — capped only.
  // Accounts for slots freed up by pending deletions.
  const remainingSlots = () =>
    maxImages - (existingImages.length - deletedSlots.size) - stagedFiles.length;

  const addBannerFiles = (fileList) => {
    const files = Array.from(fileList);

    if (!isCapped) {
      // No cap — just validate size and append.
      const valid = files.filter(validateFile);
      if (!valid.length) return;
      setStagedFiles((prev) => [...prev, ...valid.map(buildItem)]);
      return;
    }

    // ── capped: restrict to remaining slots, no auto-recycle ─────────────────
    // User must delete a saved image to free up room, same as the uncapped tab.
    const remaining = remainingSlots();

    if (remaining <= 0) {
      message.warning(
        `Already at the limit of ${maxImages}. Delete a saved image to add a new one.`,
      );
      return;
    }

    const toAdd = files.slice(0, remaining);
    const valid = toAdd.filter(validateFile);
    if (!valid.length) return;

    setStagedFiles((prev) => [...prev, ...valid.map(buildItem)]);

    if (files.length > remaining) {
      message.info(`Only ${remaining} slot(s) available — extra files ignored.`);
    }
  };

  const removeStagedBanner = (id) => {
    setStagedFiles((prev) => {
      const item = prev.find((f) => f.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const stageLogoFile = (file) => {
    if (!validateFile(file)) return;
    if (stagedFile?.preview) URL.revokeObjectURL(stagedFile.preview);
    setStagedFile(buildItem(file));
  };

  const clearStagedLogo = () => {
    if (stagedFile?.preview) URL.revokeObjectURL(stagedFile.preview);
    setStagedFile(null);
  };

  // ── BANNER (capped & uncapped): replace an existing saved image in place ───
  const openReplacePicker = (slotNum) => {
    replaceTargetSlot.current = slotNum;
    replaceInputRef.current?.click();
  };

  const handleReplaceInput = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !validateFile(file)) return;
    const slotNum = replaceTargetSlot.current;
    setReplacements((prev) => {
      const old = prev[slotNum];
      if (old?.preview) URL.revokeObjectURL(old.preview);
      return { ...prev, [slotNum]: buildItem(file) };
    });
  };

  const cancelReplacement = (slotNum) => {
    setReplacements((prev) => {
      const copy = { ...prev };
      if (copy[slotNum]?.preview) URL.revokeObjectURL(copy[slotNum].preview);
      delete copy[slotNum];
      return copy;
    });
  };

  const toggleDelete = (slotNum) => {
    setDeletedSlots((prev) => {
      const copy = new Set(prev);
      if (copy.has(slotNum)) copy.delete(slotNum);
      else copy.add(slotNum);
      return copy;
    });
  };

  // ── input / drag ───────────────────────────────────────────────────────────
  const handleFileInput = (e) => {
    if (!e.target.files?.length) return;
    if (mode === "banner") addBannerFiles(e.target.files);
    else stageLogoFile(e.target.files[0]);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (!e.dataTransfer.files?.length) return;
    if (mode === "banner") addBannerFiles(e.dataTransfer.files);
    else stageLogoFile(e.dataTransfer.files[0]);
  };

  // ── fixed URL builders ─────────────────────────────────────────────────────
  const fixedBannerUrl = (slotNum) =>
    `${IMAGE_BASE}/${originalFirmName}/${fileBaseName}${slotNum}.jpg`;

  const fixedLogoUrl = () => `${IMAGE_BASE}/${originalFirmName}/logo.jpg`;

  // ── save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (mode === "banner") {
      const hasBannerPending =
        stagedFiles.length > 0 ||
        Object.keys(replacements).length > 0 ||
        deletedSlots.size > 0;

      if (!hasBannerPending) {
        message.warning("Please select at least one image.");
        return;
      }
      setSaving(true);
      try {
        // ── unified for capped & uncapped: replace-in-place + append new + drop deleted ──
        const uploads = []; // [{ slotNum, file }]

        Object.entries(replacements).forEach(([slotNum, item]) => {
          uploads.push({ slotNum: Number(slotNum), file: item.file });
        });

        const usedSlots = existingImages.map((img) => img.slotNum);
        let nextSlot = usedSlots.length ? Math.max(...usedSlots) + 1 : 1;
        const newAssignments = stagedFiles.map((item) => {
          const slotNum = nextSlot++;
          uploads.push({ slotNum, file: item.file });
          return { slotNum, item };
        });

        for (const { slotNum, file } of uploads) {
          const fileName = `${fileBaseName}${slotNum}.jpg`;
          const fileBase = await toJpegBase64(file);
          await axios.post(
            `${CREATE_jwel1}/api/Utilities/SaveClientImages`,
            { fileName, fileBase, clientName: originalFirmName, dbId: "" },
            { headers: { "Content-Type": "application/json", tenantName } },
          );
        }

        const finalUrls = existingImages
          .filter((img) => !deletedSlots.has(img.slotNum))
          .map((img) => fixedBannerUrl(img.slotNum));
        newAssignments.forEach(({ slotNum }) =>
          finalUrls.push(fixedBannerUrl(slotNum)),
        );

        await axios.post(
          `${CREATE_jwel1}/api/Master/CommonUpdateWithWhere`,
          {
            tableName: TABLE_NAME,
            updateString: `${keyPrefix}='${finalUrls.join(",")}'`,
            whereString: `FIRMNAME='${originalFirmName}'`,
          },
          { headers: { "Content-Type": "application/json", tenantName } },
        );

        stagedFiles.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
        Object.values(replacements).forEach(
          (f) => f.preview && URL.revokeObjectURL(f.preview),
        );
        setStagedFiles([]);
        setReplacements({});
        setDeletedSlots(new Set());
        await fetchExisting();
        message.success("Images updated successfully!");
      } catch (err) {
        console.error("Save banners failed:", err);
        message.error("Failed to save banners. Please try again.");
      } finally {
        setSaving(false);
      }
    } else {
      // ── LOGO ──────────────────────────────────────────────────────────────
      if (!stagedFile) {
        message.warning("Please select an image first.");
        return;
      }
      setSaving(true);
      try {
        const fileName = `logo.jpg`;
        const fileBase = await toJpegBase64(stagedFile.file);
        await axios.post(
          `${CREATE_jwel1}/api/Utilities/SaveClientImages`,
          { fileName, fileBase, clientName: originalFirmName, dbId: "" },
          { headers: { "Content-Type": "application/json", tenantName } },
        );
        await axios.post(
          `${CREATE_jwel1}/api/Master/CommonUpdateWithWhere`,
          {
            tableName: TABLE_NAME,
            updateString: `${keyPrefix}='${fixedLogoUrl()}'`,
            whereString: `FIRMNAME='${originalFirmName}'`,
          },
          { headers: { "Content-Type": "application/json", tenantName } },
        );
        clearStagedLogo();
        await fetchExisting();
        message.success("Logo saved successfully!");
      } catch (err) {
        console.error("Save logo failed:", err);
        message.error("Failed to save logo. Please try again.");
      } finally {
        setSaving(false);
      }
    }
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const showDropZone = mode === "banner" || !stagedFile;
  const hasPending =
    mode === "banner"
      ? stagedFiles.length > 0 ||
      Object.keys(replacements).length > 0 ||
      deletedSlots.size > 0
      : !!stagedFile;

  return (
    <div className={styles.modal} style={{ "--accent": accent }}>
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
      </div>

      {/* ── Drop zone ─────────────────────────────────────────────────────── */}
      {showDropZone && (
        <div
          className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <span className={styles.plusCircle}>
            <PlusOutlined />
          </span>
          <p className={styles.dropText}>
            Drag &amp; drop or{" "}
            <span className={styles.browse}>browse file</span>
          </p>

          {mode === "banner" && isCapped && (
            <p
              className={styles.dropText}
              style={{ fontSize: 13, color: "#64748b", marginTop: -6 }}
            >
              {existingImages.length - deletedSlots.size} / {maxImages} saved
              {stagedFiles.length > 0 && ` · ${stagedFiles.length} pending`}
              {" · "}
              {remainingSlots() > 0
                ? `${remainingSlots()} slot(s) free`
                : "full — delete a saved image to add more"}
            </p>
          )}

          {mode === "banner" && !isCapped && (
            <p
              className={styles.dropText}
              style={{ fontSize: 13, color: "#64748b", marginTop: -6 }}
            >
              {existingImages.length - deletedSlots.size} saved
              {stagedFiles.length > 0 && ` · ${stagedFiles.length} new pending`}
            </p>
          )}

          <p className={styles.maxSize}>
            <WarningFilled className={styles.warnIcon} />
            Max file size : {MAX_SIZE_MB}MB
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple={mode === "banner"}
            onChange={handleFileInput}
            className={styles.hiddenInput}
          />
        </div>
      )}

      {/* hidden input used for in-place replace of a saved banner image */}
      {mode === "banner" && (
        <input
          ref={replaceInputRef}
          type="file"
          accept="image/*"
          onChange={handleReplaceInput}
          className={styles.hiddenInput}
        />
      )}

      {/* ── BANNER pending queue (new files, capped & uncapped) ────────────── */}
      {mode === "banner" && stagedFiles.length > 0 && (
        <div
          style={{
            marginTop: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <p className={styles.slotLabel} style={{ margin: "0 0 2px" }}>
            New images ({stagedFiles.length}):
          </p>
          {stagedFiles.map((item, idx) => (
            <div
              key={item.id}
              className={styles.stagedWrapper}
              style={{ margin: 0, padding: "8px 10px" }}
            >
              <div className={styles.stagedHeader}>
                <span className={styles.slotLabel}>
                  #{idx + 1} · {item.name}.{item.ext} · {item.size}
                </span>
                <button
                  className={styles.deleteBtn}
                  onClick={() => removeStagedBanner(item.id)}
                  title="Remove"
                >
                  <DeleteOutlined />
                </button>
              </div>
              {item.preview && (
                <div className={styles.carouselStage} style={{ marginTop: 6 }}>
                  <img
                    src={item.preview}
                    alt={item.name}
                    className={styles.carouselImage}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── LOGO staged preview ────────────────────────────────────────────── */}
      {mode === "logo" && stagedFile && (
        <div className={styles.stagedWrapper}>
          <div className={styles.stagedHeader}>
            <span className={styles.slotLabel}>Selected image:</span>
            <button
              className={styles.deleteBtn}
              onClick={clearStagedLogo}
              title="Remove"
            >
              <DeleteOutlined />
            </button>
          </div>
          {stagedFile.preview && (
            <div className={styles.carouselStage}>
              <img
                src={stagedFile.preview}
                alt={stagedFile.name}
                className={styles.carouselImage}
              />
            </div>
          )}
          <button
            className={styles.removeAllBtn}
            onClick={() => {
              clearStagedLogo();
              inputRef.current?.click();
            }}
            style={{ marginTop: 10 }}
          >
            Choose a different file
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            className={styles.hiddenInput}
          />
        </div>
      )}

      {/* ── Save button ───────────────────────────────────────────────────── */}
      <button
        className={styles.saveBtn}
        disabled={!hasPending || saving}
        onClick={handleSave}
      >
        {saving
          ? "Saving…"
          : mode === "banner" && stagedFiles.length > 1
            ? `Save & Upload (${stagedFiles.length})`
            : "Save & Upload"}
      </button>

      {/* ── BANNER (capped & uncapped): saved images with Replace/Delete ───── */}
      {mode === "banner" && existingImages.length > 0 && (
        <div
          style={{
            marginTop: 16,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <p className={styles.slotLabel} style={{ margin: "0 0 2px" }}>
              Saved images ({existingImages.length}):
            </p>
            {hasPending && (
              <button
                className={styles.saveBtn}
                style={{ width: "auto", padding: "6px 16px", margin: 0 }}
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            )}
          </div>
          {existingImages.map((img) => {
            const isDeleted = deletedSlots.has(img.slotNum);
            const replacement = replacements[img.slotNum];
            return (
              <div
                key={img.slotNum}
                className={styles.stagedWrapper}
                style={{
                  margin: 0,
                  padding: "8px 10px",
                  opacity: isDeleted ? 0.5 : 1,
                }}
              >
                <div className={styles.stagedHeader}>
                  <span className={styles.slotLabel}>
                    {fileBaseName}
                    {img.slotNum}.jpg
                    {isDeleted && " · marked for removal"}
                    {replacement &&
                      !isDeleted &&
                      ` · replacing with ${replacement.name}.${replacement.ext}`}
                  </span>
                  <div style={{ display: "flex", gap: 8 }}>
                    {!isDeleted && (
                      <button
                        className={styles.deleteBtn}
                        onClick={() => openReplacePicker(img.slotNum)}
                        title="Replace"
                      >
                        <SwapOutlined />
                      </button>
                    )}
                    {replacement && (
                      <button
                        className={styles.deleteBtn}
                        onClick={() => cancelReplacement(img.slotNum)}
                        title="Undo replace"
                      >
                        ✕
                      </button>
                    )}
                    <button
                      className={styles.deleteBtn}
                      onClick={() => toggleDelete(img.slotNum)}
                      title={isDeleted ? "Undo delete" : "Delete"}
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                </div>
                <div className={styles.carouselStage} style={{ marginTop: 6 }}>
                  <img
                    src={replacement?.preview || img.url}
                    alt={`${fileBaseName}${img.slotNum}`}
                    className={styles.carouselImage}
                  />
                </div>
              </div>
            );
          })}
          {hasPending && (
            <button
              className={styles.saveBtn}
              disabled={saving}
              onClick={handleSave}
              style={{ marginTop: 4 }}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          )}
        </div>
      )}

      {/* ── LOGO plain block (saved image) ────────────────────────────────── */}
      {mode === "logo" && existingLogo && (
        <div className={styles.carousel} style={{ marginTop: 16 }}>
          <div className={styles.carouselHead}>
            <span className={styles.carouselTitle}>Current Logo</span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "16px",
              background: "#f8fafc",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
            }}
          >
            <img
              src={existingLogo}
              alt="Firm Logo"
              style={{
                maxHeight: 120,
                maxWidth: "100%",
                objectFit: "contain",
                borderRadius: 4,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};